import { Vehicle } from 'api3';
import { CommonModule } from '@angular/common';
import { Component, ViewChild, computed, inject, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DisplayVehicle } from '@app/services/types';
import { VehicleService } from '@app/services/vehicle.service';
import { TranslocoPipe } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { Button } from 'primeng/button';

type VinKbaSearchInput = {
  vin: string | undefined;
  kba: string | undefined;
};

@Component({
  selector: 'app-vin-kba-selection',
  standalone: true,
  imports: [CommonModule, TranslocoPipe, Button, FormsModule, AutoCompleteModule],
  templateUrl: './vin-kba-selection.component.html',
})
export class VinKbaSelectionComponent {
  onSelect = output<DisplayVehicle | undefined>();

  VIN_LENGTH = 17;
  isLoading = signal(false);
  vehicleIdentification = signal<VinKbaSearchInput>({ vin: undefined, kba: undefined });
  inputValue = signal<string | Vehicle | undefined>(undefined);
  selectedVehicle = signal<Vehicle | undefined>(undefined);
  foundVehicles = signal<Vehicle[]>([]);

  @ViewChild('autocomplete') autocomplete!: AutoComplete;
  @ViewChild('submitButtonVinKba') submitButton!: Button;
  searchMethod = signal<'vin' | 'kba' | undefined>(undefined);
  error = signal<'global' | 'format' | undefined>(undefined);

  isInvalid = computed(
    () =>
      this.error() === 'global' ||
      (this.error() === 'format' &&
        this.inputValue() !== undefined &&
        typeof this.inputValue() === 'string' &&
        (this.inputValue() as string).length >= 6),
  );

  isTooLong = computed(() => {
    const value = this.inputValue();
    return value && typeof value === 'string' && value.length > this.VIN_LENGTH;
  });

  vehiclesService = inject(VehicleService);

  onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === 'NumpadEnter') {
      if (!this.inputValue()) {
        this.showDropdown();
      }
    }
  }

  onInputFocus(): void {
    if (this.foundVehicles().length > 0) {
      this.autocomplete.suggestions = this.foundVehicles();
      this.showDropdown();
    }
  }

  showDropdown(): void {
    if (this.autocomplete && this.foundVehicles().length > 0) {
      this.autocomplete.show();
    }
  }

  public clear() {
    this.inputValue.set(undefined);
    this.foundVehicles.set([]);
    this.autocomplete.clear();
    this.searchMethod.set(undefined);

    this.error.set(undefined);
    this.hideDropdown();
  }

  public hideDropdown() {
    this.autocomplete.hide();
  }

  findVehicles(event: AutoCompleteCompleteEvent) {
    this.isLoading.set(true);

    if (this.selectedVehicle()) {
      this.inputValue.set(this.vehicleIdentification().vin || this.vehicleIdentification().kba || undefined);
    }
    this.selectedVehicle.set(undefined);

    this._search(event.query)
      .then((result) => {
        this.foundVehicles.set(result);
        this.autocomplete.suggestions = result;
        this.isLoading.set(false);

        if (result.length > 0) {
          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
              this.autocomplete.show();
              this.autocomplete.focusedOptionIndex.set(0);
            });
          } else {
            setTimeout(() => {
              this.autocomplete.show();
              this.autocomplete.focusedOptionIndex.set(0);
            }, 0);
          }
        }
      })
      .finally(() => {
        this.isLoading.set(false);
      });
  }

  handleSelect() {
    this.selectedVehicle.set(this.inputValue() as Vehicle);
    setTimeout(() => {
      this.autocomplete.hide();
      const button = this.submitButton.el?.nativeElement.querySelector('button');
      button?.focus();
    }, 200);
  }

  /**
   * Final selection
   * (add identification data)
   */
  onFinishSelection() {
    if (!this.inputValue() || typeof this.inputValue() === 'string') {
      console.warn('[VinKbaSelectionComponent] onFinishSelection called without valid vehicle selection');
      return;
    }

    const vehicle = this.inputValue() as Vehicle;

    const vehicleToSubmit: DisplayVehicle = {
      ...vehicle,
      vehicleIdentification: {
        ...this.vehicleIdentification(),
        id: vehicle.id,
      },
    };
    this.onSelect.emit(vehicleToSubmit);
  }

  private async _search(searchKey: string) {
    this.error.set(undefined);

    const regexVin = /^[A-HJ-NPR-Za-hj-npr-z0-9]{17}$/;
    const regexKba = /^[0-9]{4}[0-9a-zA-Z]{3}$/;
    const trimmedInput = searchKey.trim().replace(/[^a-zA-Z0-9:]/g, '');

    if (regexVin.test(trimmedInput)) {
      this.searchMethod.set('vin');
      this.vehicleIdentification.set({ vin: trimmedInput, kba: undefined });
      return await this._getVehiclesByVin(trimmedInput);
    } else if (regexKba.test(trimmedInput)) {
      this.searchMethod.set('kba');
      this.vehicleIdentification.set({ vin: undefined, kba: trimmedInput });
      return await this._getVehiclesByKba(trimmedInput);
    } else {
      this.searchMethod.set(undefined);
      this.vehicleIdentification.set({ vin: undefined, kba: undefined });
      this.error.set('format');
      console.warn('Invalid VIN/KBA format ;-(');
      return [];
    }
  }

  private async _getVehiclesByVin(vin: string): Promise<Vehicle[]> {
    this.isLoading.set(true);
    const { data, error } = await this.vehiclesService.getVehiclesByVIN(vin);
    this.isLoading.set(false);

    if (error) {
      this.error.set('global');
      console.error('[VinKbaSelectionComponent] Error:', error);

      const sentryError = new Error(`Error finding vehicle by vin ${vin}: ${error?.message || 'Unknown error'}`);
      Sentry.captureException(sentryError, {
        tags: {
          location: 'VinKbaSearchComponent',
          feature: 'VehicleIdentification',
          message: 'Error finding vehicle by vin ' + vin,
        },
        extra: {
          originalError: error,
          searchType: 'vin',
          statusCode: error?.statusCode,
          path: error?.path,
        },
      });
    }

    const enhancedData = (data || []).map((vehicle) => ({
      ...vehicle,
      displayString: this.vehiclesService.getTypeString(vehicle as Vehicle),
    }));

    return enhancedData;
  }

  private async _getVehiclesByKba(kba: string): Promise<Vehicle[]> {
    this.isLoading.set(true);
    const { data, error } = await this.vehiclesService.getVehiclesByKBA(kba);
    this.isLoading.set(false);

    if (error) {
      this.error.set('global');
      console.error('[VinKbaSelectionComponent] Error:', error);
      Sentry.captureException(error, {
        tags: {
          location: 'VinKbaSelectionComponent',
          feature: 'VehicleIdentification',
          message: 'Error finding vehicle by kba' + kba,
        },
      });
    }

    const enhancedData = (data || []).map((vehicle) => ({
      ...vehicle,
      displayString: this.vehiclesService.getTypeString(vehicle as Vehicle),
    }));

    return enhancedData;
  }
}
